import { isNull } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';

import { type TranscriptSegmentData } from '~/components/AssetModalV2/Content/Panel/Transcript/types';
import { isInRange } from '~/components/AssetModalV2/Content/Panel/Transcript/utils';
import { useVideoTimestampObserverContext } from '~/providers/VideoTimestampObserverProvider';

export type OnCurrentSegmentChanged = (segmentIndex: number) => void;

type UseFindCurrentSegmentIndexParams = {
  segments: TranscriptSegmentData[];
  callback: OnCurrentSegmentChanged;
};

export const useFindCurrentSegmentIndex = ({ segments, callback }: UseFindCurrentSegmentIndexParams) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const currentSegmentIndex = useRef(-1);

  const { subscribe, getCurrentTime } = useVideoTimestampObserverContext();

  const findCurrentSegmentIndex = useCallback(
    (timeInMs: number) => {
      if (currentSegmentIndex.current > -1) {
        const { startTimeMs, endTimeMs } = segments[currentSegmentIndex.current];
        if (isInRange(timeInMs, [startTimeMs, endTimeMs])) {
          return;
        }
      }

      // user may set time on a video that is not bound to any segment
      // in that case we want to pick the closest segment after that time
      let minPastTime = Number.MAX_SAFE_INTEGER;
      let newSegmentIndex = -1;

      for (let index = 0; index < segments.length; index++) {
        const { startTimeMs, endTimeMs } = segments[index];
        if (isInRange(timeInMs, [startTimeMs, endTimeMs])) {
          newSegmentIndex = index;
          break;
        } else if (startTimeMs > timeInMs) {
          const pastTime = startTimeMs - timeInMs;
          if (pastTime < minPastTime) {
            minPastTime = pastTime;
            newSegmentIndex = index;
          }
        }
        if (endTimeMs > timeInMs) {
          break;
        }
      }

      if (currentSegmentIndex.current === newSegmentIndex) return;
      currentSegmentIndex.current = newSegmentIndex;

      if (currentSegmentIndex.current !== -1) {
        callbackRef.current(currentSegmentIndex.current);
      } else {
        currentSegmentIndex.current = -1;
      }
    },
    [segments],
  );

  useEffect(() => {
    const currentTime = getCurrentTime();
    if (isNull(currentTime)) return;
    findCurrentSegmentIndex(currentTime * 1000);
  }, [findCurrentSegmentIndex, getCurrentTime]);

  useEffect(() => {
    const subscription = subscribe((timeInSeconds) => {
      if (isNull(timeInSeconds)) return;
      findCurrentSegmentIndex(timeInSeconds * 1000);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [findCurrentSegmentIndex, segments, subscribe]);
};
